export interface Document {
  id: string;
  filename: string;
  title: string;
  author: string;
  description: string;
  tags: string[];
  sourceType: string;
  sourceOrganisation: string;
  country: string;
  year: string;
  evidenceType: string;
  dataRetention: string | null;
  url: string;
  language: string;
  ownership: string;
  clientId: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface FieldValue {
  value: string;
  status: string;
}

export interface PicoSubgroup {
  population: string;
  intervention: string;
  comparator: string;
  outcome: string;
  fields?: Record<string, string | FieldValue>;
}

export interface PicoExtraction {
  id: string;
  studyName: string;
  authors: string;
  countries: string[];
  year: string;
  sourceType: string;
  sourceOrganisation: string;
  studyId: string;
  status: string;
  subgroups: PicoSubgroup[];
  errorMessage?: string;
}

export interface Project {
  id: string;
  name: string;
  code: string;
  kantataProjectId: string | null;
  clientIds: string[];
  clientNames: string[];
  status: string;
  startDate: string;
  teamMembers: number;
  therapyArea: string;
  indication: string;
  condition: string;
  objective: string;
  projectType: string;
  model: string;
  dataRetentionDefault: string | null;
  documents: Document[];
  picoExtractions: PicoExtraction[];
  completion: number;
  daysInProgress: number;
  memberIds: string[];
  createdBy: string | null;
  createdAt: string;
}

export interface Template {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  template_type_id: number | null;
  template_formatetype_ids: number[] | null;
  created_at: string;
  updated_at: string;
}

export interface PromptLibrary {
  id: string;
  name: string;
  description: string | null;
  priming: string | null;
  model: string | null;
  temperature: number | null;
  is_active: boolean;
  image: string | null;
  config: string | null;
  template_type_id: number | null;
  template_formatetype_ids: number[] | null;
  created_at: string;
  updated_at: string;
}

export interface TemplatePromptLibrary {
  id: string;
  template_id: string;
  prompt_library_id: string;
  ordinal: number | null;
  created_at: string;
  updated_at: string;
}

export interface TemplateFormValues {
  name: string;
  description: string;
  is_active: boolean;
}

export interface PromptLibraryFormValues {
  name: string;
  priming: string;
}

export interface TemplateDocumentData {
  id: string;
  projectId: string;
  projectTemplateId: string;
  documentId: string;
  data: string | null;
  status: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateDocumentDataCreate {
  document_id: string;
  data: string | null;
  status: string;
}

export const OWNERSHIP_VALUES = ["Private", "Client"] as const; // "Public" hidden: to restore add "Public" as first element
export type OwnershipValue = typeof OWNERSHIP_VALUES[number];

export type ReportJobStatus = "Queued" | "Processing" | "Processed" | "Error";

export interface ExtractionColumnDetail {
  document_id: string;
  column_name: string;
  column_value: string;
}

export interface ReportEnqueueResponse {
  sentinel_id: string;
  job_id: string;
  project_id: string;
  template_id: string;
  prompt_library_id: string;
}

export interface ReportStatusResponse {
  status: ReportJobStatus | "NotStarted";
  data?: string;
  error?: string;
  queue_position?: number;
  queue_size?: number;
}

export interface PromptLibraryUsage {
  prompt_library_id: string;
  template_count: number;
  templates: Array<{ id: string; name: string }>;
}
